/**
 * Supported UI display languages for the authenticated app.
 *
 * This is the single source of truth for the locale list — i18n init, the
 * language selector, and the catalog-parity test all read from here. Adding a
 * locale = add an entry here, add a matching `locales/<code>/common.json`, and add
 * a block to `MESSAGES` in the API. The backend's `VALID_UI_LANGUAGES` and its
 * `ui_language` pattern derive from that block, so there is nothing else to widen.
 *
 * NOTE: this is the *interface* language only. It is unrelated to the AI
 * response language (UserSettings.response_language) or the detected language
 * of review content.
 */

export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pt', label: 'Português' },
] as const

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]['code']

export const SUPPORTED_LOCALE_CODES: LocaleCode[] = SUPPORTED_LOCALES.map((l) => l.code)

export const DEFAULT_LOCALE: LocaleCode = 'en'

/** Narrow an arbitrary string to a supported locale, falling back to default. */
export function toSupportedLocale(value: string | null | undefined): LocaleCode {
  const primary = (value ?? '').split('-')[0].toLowerCase()
  return (SUPPORTED_LOCALE_CODES as string[]).includes(primary)
    ? (primary as LocaleCode)
    : DEFAULT_LOCALE
}
